import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useVirtualTours, usePropertyField } from '@vibesites/runtime/react';
import pagesData from '@vibesites/theme-preferred/pages.json';

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
}

/**
 * Dietz override of the parent SitemapContent.
 *
 * Mirrors the dynamic nav rules in Header.tsx: hide the Virtual Tour and
 * Interactive Map links when their backing property data is missing.
 *
 * - `virtual-tour` is hidden when the property has zero virtual tours with
 *   a usable URL (matches Header + the runtime noindex on the VT page).
 * - `interactive-map` is hidden when the property has no Engrain SightMap
 *   URL (matches Header).
 *
 * Other low-priority pages (privacy, accessibility, thank-you, etc.) stay
 * in the sitemap regardless of pages.json `visible: false` — sitemaps are
 * intentionally exhaustive for legal/utility pages.
 */
export function SitemapContent() {
  const tours = useVirtualTours() as Array<{ virtual_tour_url?: string | null }>;
  const sightmapUrl = usePropertyField('custom_engrain_sightmap_url') as string | null;

  const hasVirtualTours = Array.isArray(tours) && tours.some((t) => t && t.virtual_tour_url);
  const hasSightmap = Boolean(sightmapUrl && String(sightmapUrl).trim());

  const pages = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      // Don't link the sitemap to itself
      if (page.path === '/sitemap') return false;
      // Templates are expanded dynamically; their unresolved {{placeholders}} are not user-facing
      if (page.template) return false;
      // Property-data-driven hides — match Header behavior
      if (alias === 'virtual-tour') return hasVirtualTours;
      if (alias === 'interactive-map') return hasSightmap;
      return true;
    })
    .sort((a, b) => a[1].position - b[1].position)
    .map(([_, page]) => page);

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="sitemapcontent.heading"
          as="h1"
          className="font-heading text-2xl sm:text-3xl font-normal text-black mb-8"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:bg-black hover:text-white transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'richtext',
      defaultValue: '<p>Site Map</p>',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SitemapContent;
